import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, RotateCcw, RefreshCw, Clock, Lock, CreditCard, ShieldCheck,
  AlertTriangle, Download, Settings2, X, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import usePlanChange from "@/components/billing/usePlanChange";
import CompactPlanGrid from "@/components/billing/CompactPlanGrid";
import ChangeBar from "@/components/billing/ChangeBar";
import ConfirmChangeModal from "@/components/billing/ConfirmChangeModal";
import EnterpriseModal from "@/components/billing/EnterpriseModal";
import UsageCard from "@/components/billing/UsageCard";
import { cn, dateLocale } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

function NonOwnerBillingBanner() {
  const { user } = useAuth();
  if (!user || user.workspace_role === "OWNER") return null;
  return (
    <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-4 text-sm" data-testid="billing-nonowner-banner" role="status">
      {tfr("Seul le propriétaire du workspace gère l'abonnement.")}{" "}
      {tfr("Vous consultez la facturation du workspace")} {user.workspace_name ? `« ${user.workspace_name} »` : ""}.
    </div>
  );
}

/* ----------------------- Helpers ----------------------- */
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(dateLocale(), { day: "2-digit", month: "long", year: "numeric" });
  } catch (e) { return "—"; }
}
function fmtMoney(cents, currency = "EUR") {
  try {
    return new Intl.NumberFormat(dateLocale(), { style: "currency", currency }).format((cents || 0) / 100);
  } catch (e) {
    return `${((cents || 0) / 100).toFixed(2)} €`;
  }
}

function StatusBadge({ state, cancelAtPeriodEnd }) {
  const map = {
    active: ["bg-emerald-500/10 text-emerald-400 border-emerald-500/25", "Actif"],
    trialing: ["bg-sky-500/10 text-sky-400 border-sky-500/25", tfr("Essai en cours")],
    past_due: ["bg-amber-500/10 text-amber-400 border-amber-500/25", tfr("Paiement échoué")],
    canceled_active: ["bg-amber-500/10 text-amber-400 border-amber-500/25", tfr("Annulation programmée")],
    expired: ["bg-rose-500/10 text-rose-400 border-rose-500/25", tfr("Expiré")],
  };
  const [cls, label] = map[state] || ["bg-foreground/5 text-muted-foreground border-border", state || "—"];
  return (
    <span
      className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium border tabular-nums", cls)}
      data-testid="billing-status-badge"
    >
      <span className={cn(
        "h-1.5 w-1.5 rounded-full",
        state === "active" && "bg-emerald-400",
        state === "trialing" && "bg-sky-400",
        (state === "past_due" || state === "canceled_active") && "bg-amber-400",
        state === "expired" && "bg-rose-400",
      )} />
      {label}{cancelAtPeriodEnd && state === "active" ? ` · ${tfr("Résilié")}` : ""}
    </span>
  );
}

function Card({ className, children, ...props }) {
  return (
    <section
      className={cn(
        "rounded-2xl border border-border bg-surface",
        "shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.18)]",
        className
      )}
      {...props}
    >
      {children}
    </section>
  );
}

const INVOICE_STATUS = {
  paid: [tfr("Payée"), "bg-emerald-500/10 text-emerald-400 border-emerald-500/25"],
  open: ["Ouverte", "bg-sky-500/10 text-sky-400 border-sky-500/25"],
  draft: [tfr("En attente"), "bg-foreground/5 text-muted-foreground border-border"],
  uncollectible: [tfr("Échouée"), "bg-rose-500/10 text-rose-400 border-rose-500/25"],
  void: [tfr("Annulée"), "bg-foreground/5 text-muted-foreground border-border"],
};

/* ----------------------- Page ----------------------- */
export default function Billing() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [pmLoaded, setPmLoaded] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invLoaded, setInvLoaded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/billing/status");
      setStatus(r.data);
    } catch (e) {
      toast.error(tfr("Erreur chargement abonnement"));
    } finally {
      setLoading(false);
    }
  }, []);

  const planFlow = usePlanChange({ onChanged: async () => { await load(); await checkAuth(); } });

  useEffect(() => { load(); }, [load]);

  // Non-blocking: payment method + invoices
  useEffect(() => {
    api.get("/billing/payment-method").then((r) => setPaymentMethod(r.data?.payment_method || null)).catch(() => {}).finally(() => setPmLoaded(true));
    api.get("/billing/invoices").then((r) => setInvoices(r.data?.invoices || [])).catch(() => {}).finally(() => setInvLoaded(true));
  }, []);

  // Preserve selection when returning from /billing/plans (?select=<id>)
  useEffect(() => {
    const sel = searchParams.get("select");
    if (sel && planFlow.data && sel !== planFlow.current) planFlow.selectPlan(sel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFlow.data]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    const canceled = params.get("canceled");
    const success = params.get("success");

    if (canceled) {
      toast.info(tfr("Paiement annulé"));
      navigate("/billing", { replace: true });
      return;
    }

    if (success === "1" || success === "true") {
      setPending(true);
      navigate("/billing", { replace: true });
      let attempts = 0;
      const poll = async () => {
        attempts++;
        await checkAuth();
        try {
          const r = await api.get("/billing/status");
          setStatus(r.data);
          if (r.data?.state === "active") {
            toast.success(tfr("Votre abonnement est maintenant actif !"));
            setPending(false);
            return;
          }
        } catch (_) { /* ignore */ }
        if (attempts < 12) setTimeout(poll, 2500);
        else setPending(false);
      };
      poll();
      return;
    }

    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const r = await api.get(`/billing/checkout/status/${sessionId}`);
        if (r.data?.status === "complete" && r.data?.payment_status === "paid") {
          toast.success(tfr("Votre abonnement est maintenant actif !"));
          await checkAuth();
          await load();
          navigate("/billing", { replace: true });
          return;
        }
        if (r.data?.status === "expired") {
          toast.error(tfr("Session de paiement expirée"));
          navigate("/billing", { replace: true });
          return;
        }
      } catch (_) { /* ignore */ }
      if (attempts < 8) setTimeout(poll, 2000);
      else {
        await load();
        navigate("/billing", { replace: true });
      }
    };
    poll();
  }, [location.search, load, checkAuth, navigate]);

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portail Stripe indisponible");
      setBusy(false);
    }
  };

  const cancelSub = async () => {
    if (!window.confirm(tfr("Résilier votre abonnement ? Vous gardez l'accès jusqu'à la fin de la période en cours."))) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success(tfr("Abonnement résilié"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post("/billing/reactivate");
      toast.success(tfr("Abonnement réactivé"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-[1120px] mx-auto px-7 py-7" data-testid="billing-skeleton">
        <div className="h-4 w-24 bg-secondary/50 rounded animate-pulse mb-3" />
        <div className="h-8 w-64 bg-secondary/50 rounded animate-pulse mb-8" />
        <div className="h-52 bg-secondary/50 rounded-2xl animate-pulse mb-6" />
        <div className="h-72 bg-secondary/50 rounded-2xl animate-pulse" />
      </div>
    );
  }

  const state = status?.state;
  const isActive = state === "active";
  const isTrialing = state === "trialing";
  const isExpired = state === "expired";
  const isPastDue = state === "past_due";
  const isCanceledActive = state === "canceled_active";
  const hasSub = isActive || isPastDue || isCanceledActive;

  return (
    <div className="w-full max-w-[1120px] mx-auto px-4 sm:px-7 pt-7 pb-12 space-y-6" data-testid="billing-page">
      {/* HEADER */}
      <header>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em]">{tfr("Facturation")}</h1>
        <p className="text-sm text-muted-foreground mt-1.5">{tfr("Gérez votre abonnement, vos paiements et vos factures.")}</p>
      </header>

      <NonOwnerBillingBanner />

      {/* PENDING ACTIVATION */}
      {pending && (
        <div className="rounded-xl border border-sky-500/25 bg-sky-500/[0.05] p-4 flex items-start gap-3" data-testid="billing-pending-banner" role="status">
          <Clock className="h-5 w-5 text-sky-400 shrink-0 mt-0.5 animate-pulse" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{tfr("Paiement reçu, activation en cours…")}</p>
            <p className="text-xs text-muted-foreground mt-1">{tfr("Stripe nous confirme votre paiement. L'activation est généralement immédiate.")}</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5 shrink-0" data-testid="billing-pending-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Vérifier
          </Button>
        </div>
      )}

      {/* PAST DUE WARNING */}
      {isPastDue && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-200/90">
            {tfr("Votre dernier paiement a échoué. Mettez à jour votre carte pour éviter l'interruption.")}
          </div>
        </div>
      )}

      {/* CURRENT PLAN CARD */}
      <Card className="p-6 sm:p-8" data-testid="billing-hero-card">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{tfr("Plan actuel")}</div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em] mt-1.5" data-testid="billing-plan-name">
              {status?.plan_name || "Athlynx Pro"}
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-md">
              {tfr(status?.plan_tagline) || tfr("L'espace de travail complet pour gérer vos athlètes, opportunités, documents et opérations.")}
            </p>
          </div>

          <div className="md:text-right shrink-0 space-y-2.5">
            <div className="flex md:justify-end items-baseline gap-1">
              <span className="text-3xl sm:text-4xl font-semibold tabular-nums tracking-[-0.02em]" data-testid="billing-plan-price">
                {fmtMoney(status?.amount_cents, status?.currency?.toUpperCase() || "EUR")}
              </span>
              <span className="text-sm text-muted-foreground">{tfr("/ mois")}</span>
            </div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{tfr("Facturé mensuellement")}</div>
            {status?.is_legacy_price && (
              <div className="text-[11px] text-amber-300/90" data-testid="billing-legacy-price">
                Tarif historique : {fmtMoney(status.amount_cents, status?.currency?.toUpperCase() || "EUR")} / mois
                {" · "}Tarif catalogue : {fmtMoney(status.catalog_amount_cents, status?.currency?.toUpperCase() || "EUR")}
              </div>
            )}
            <div className="flex md:justify-end">
              <StatusBadge state={state} cancelAtPeriodEnd={status?.cancel_at_period_end} />
            </div>
            {isTrialing && (
              <div className="text-xs text-muted-foreground md:text-right" data-testid="billing-trial-info">
                {tfr("Essai jusqu'au")} <span className="text-foreground/80 font-medium">{fmtDate(status.trial_ends_at)}</span>
                {" · "}
                <span className="text-sky-400 font-medium">
                  {status.trial_days_left} {status.trial_days_left > 1 ? tfr("jours") : tfr("jour")}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 sm:mt-8 pt-5 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-muted-foreground space-y-1 min-w-0">
            {isTrialing && <p>{tfr("Activez maintenant pour conserver l'accès après l'essai.")}</p>}
            {isActive && status?.current_period_end && (
              <p>{tfr("Renouvellement le")} <span className="text-foreground/80 font-medium">{fmtDate(status.current_period_end)}</span></p>
            )}
            {isCanceledActive && status?.current_period_end && (
              <p>{tfr("Fin d'accès le")} <span className="text-foreground/80 font-medium">{fmtDate(status.current_period_end)}</span></p>
            )}
            {isExpired && <p>{tfr("Votre essai est terminé. Réactivez pour continuer.")}</p>}
            {isPastDue && <p>{tfr("Mettez à jour votre moyen de paiement.")}</p>}
            {status?.pending_plan_id && (
              <p className="text-amber-300/90 flex items-center gap-2 flex-wrap" data-testid="billing-pending-change">
                {tfr("Passage à")} <span className="font-medium">{status.pending_plan_name}</span> {tfr("le")} {fmtDate(status.pending_plan_effective)}
                <button
                  onClick={planFlow.cancelScheduled}
                  disabled={planFlow.busy}
                  className="underline underline-offset-2 hover:text-amber-200 transition-colors"
                  data-testid="billing-cancel-change-btn"
                >
                  {tfr("Annuler le changement")}
                </button>
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 sm:justify-end shrink-0">
            {(isTrialing || isExpired) && (
              <Button
                onClick={() => planFlow.activateNow(planFlow.current)}
                disabled={planFlow.busy}
                className="gap-2 bg-foreground text-background hover:bg-foreground/90 active:scale-[0.97] transition-all h-11"
                data-testid="billing-hero-cta"
              >
                {planFlow.busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {isTrialing ? tfr("Activer maintenant") : tfr("Réactiver")} <ArrowRight className="h-4 w-4" />
              </Button>
            )}
            {isActive && (
              <Button variant="outline" onClick={openPortal} disabled={busy} className="gap-2 h-11" data-testid="billing-manage-btn">
                <Settings2 className="h-4 w-4" /> {tfr("Gérer l'abonnement")}
              </Button>
            )}
            {isPastDue && (
              <Button onClick={openPortal} disabled={busy} className="gap-2 h-11" data-testid="billing-update-payment-btn">
                <CreditCard className="h-4 w-4" /> {tfr("Mettre à jour le paiement")}
              </Button>
            )}
            {isCanceledActive && (
              <Button variant="outline" onClick={reactivate} disabled={busy} className="gap-2 h-11" data-testid="billing-reactivate-btn">
                <RotateCcw className="h-4 w-4" /> {tfr("Réactiver l'abonnement")}
              </Button>
            )}
          </div>
        </div>
      </Card>

      <UsageCard />

      {/* CHANGER D'OFFRE */}
      <Card className="p-6 sm:p-8" data-testid="billing-plan-section" id="plan-selector-section">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-5">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">{tfr("Changer d'offre")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{tfr("Choisissez l'offre qui correspond le mieux à vos besoins.")}</p>
          </div>
          <button
            onClick={() => navigate(`/billing/plans${planFlow.selected ? `?select=${planFlow.selected}` : ""}`)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-400 hover:text-sky-600 dark:text-sky-300 transition-colors shrink-0"
            data-testid="billing-compare-link"
          >
            {tfr("Comparer les offres en détail")} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {planFlow.loadError ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="plans-error">
            {tfr("Impossible de charger le catalogue des offres.")}
            <Button variant="outline" size="sm" className="ml-3" onClick={planFlow.load}>{tfr("Réessayer")}</Button>
          </div>
        ) : !planFlow.data ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5" data-testid="plans-loading">
            {[0, 1, 2].map((i) => <div key={i} className="h-44 rounded-[14px] bg-secondary/40 animate-pulse" />)}
          </div>
        ) : (
          <CompactPlanGrid
            plans={planFlow.plans}
            current={planFlow.current}
            selected={planFlow.selected}
            isTrial={planFlow.isTrial}
            pendingPlanId={planFlow.data.pending_plan_id}
            unavailablePlans={planFlow.unavailablePlans}
            onSelect={planFlow.selectPlan}
            onEnterprise={() => planFlow.setEnterpriseOpen(true)}
          />
        )}
      </Card>

      {/* MOYEN DE PAIEMENT */}
      <Card className="p-6 sm:p-8" data-testid="billing-payment-method">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold tracking-tight">{tfr("Moyen de paiement")}</h3>
          {hasSub && paymentMethod && (
            <Button variant="outline" size="sm" onClick={openPortal} disabled={busy} className="gap-1.5 h-9" data-testid="billing-update-payment">
              {tfr("Mettre à jour")}
            </Button>
          )}
        </div>

        {!pmLoaded ? (
          <div className="h-16 rounded-xl bg-secondary/40 animate-pulse" />
        ) : paymentMethod ? (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-secondary/30 px-4 py-3" data-testid="payment-method-card">
            <div className="h-9 w-12 rounded-md bg-gradient-to-br from-sky-500/15 to-indigo-600/15 border border-border flex items-center justify-center shrink-0">
              <CreditCard className="h-4 w-4 text-foreground/70" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium capitalize">
                {paymentMethod.brand} •••• {paymentMethod.last4}
              </div>
              <div className="text-xs text-muted-foreground">
                Expire {String(paymentMethod.exp_month).padStart(2, "0")}/{paymentMethod.exp_year}
              </div>
            </div>
            {paymentMethod.is_default && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-border text-foreground/70 shrink-0">{tfr("Par défaut")}</span>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-secondary/20 p-5" data-testid="payment-empty">
            <p className="text-sm text-muted-foreground">
              {tfr("Aucun moyen de paiement enregistré. Il sera ajouté de manière sécurisée via Stripe lors de l'activation.")}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 gap-1.5"
              disabled={busy || planFlow.busy}
              onClick={() => (hasSub ? openPortal() : planFlow.activateNow(planFlow.current))}
              data-testid="billing-add-payment-btn"
            >
              <CreditCard className="h-3.5 w-3.5" /> Ajouter un moyen de paiement
            </Button>
          </div>
        )}

        <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="h-3 w-3" /> Paiements sécurisés par Stripe
        </div>
      </Card>

      {/* HISTORIQUE DES FACTURES */}
      <Card className="p-6 sm:p-8" data-testid="billing-invoices-card">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold tracking-tight">{tfr("Historique des factures")}</h3>
          {hasSub && (
            <button
              onClick={openPortal}
              className="text-xs font-medium text-sky-400 hover:text-sky-600 dark:text-sky-300 inline-flex items-center gap-1 transition-colors"
              data-testid="billing-view-all-invoices"
            >
              Voir toutes les factures <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>

        {!invLoaded ? (
          <div className="h-16 rounded-xl bg-secondary/40 animate-pulse" />
        ) : invoices.length > 0 ? (
          <div className="rounded-xl border border-border divide-y divide-border/60" data-testid="invoice-list">
            {invoices.map((inv, i) => {
              const [label, cls] = INVOICE_STATUS[inv.status] || [inv.status, "bg-foreground/5 text-muted-foreground border-border"];
              return (
                <div key={inv.id} className="flex items-center gap-3 px-4 py-3" data-testid={`invoice-row-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{inv.number || inv.id}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(inv.created)}</div>
                  </div>
                  <div className="text-sm font-medium tabular-nums shrink-0">
                    {fmtMoney(inv.amount_cents, (inv.currency || "eur").toUpperCase())}
                  </div>
                  <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0", cls)}>{label}</span>
                  {inv.pdf_url && (
                    <a
                      href={inv.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors shrink-0"
                      aria-label={`${tfr("Télécharger la facture")} ${inv.number || inv.id}`}
                      data-testid={`invoice-download-${i}`}
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-secondary/30 p-8 text-center" data-testid="invoices-empty">
            <CreditCard className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm font-medium">{tfr("Aucune facture pour le moment")}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {tfr("Vos factures apparaîtront ici après votre premier paiement.")}
            </p>
          </div>
        )}
      </Card>

      {/* Secondary actions + trust footer */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" /> {tfr("Conformité Stripe · TVA gérée automatiquement")}
        </div>
        {isActive && !status?.cancel_at_period_end && (
          <button
            onClick={cancelSub}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-300/80 hover:text-rose-300 transition-colors"
            data-testid="billing-cancel-btn"
          >
            <X className="h-3.5 w-3.5" /> Annuler l'abonnement
          </button>
        )}
      </div>

      {/* Sticky confirmation bar */}
      <ChangeBar
        currentPlan={planFlow.currentPlan}
        selectedPlan={planFlow.selectedPlan}
        changeMode={planFlow.changeMode}
        isTrial={planFlow.isTrial}
        busy={planFlow.busy}
        onCancel={planFlow.clearSelection}
        onDetails={() => navigate(`/billing/plans?select=${planFlow.selected}`)}
        onConfirm={planFlow.openConfirm}
        onActivate={() => planFlow.activateNow(planFlow.selected)}
      />

      <ConfirmChangeModal
        open={planFlow.confirmOpen}
        onOpenChange={planFlow.setConfirmOpen}
        preview={planFlow.preview}
        plans={planFlow.plans}
        busy={planFlow.busy}
        onConfirm={planFlow.confirmChange}
      />
      <EnterpriseModal open={planFlow.enterpriseOpen} onClose={() => planFlow.setEnterpriseOpen(false)} />
    </div>
  );
}
